/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api.ts
 🗓️ Created: 2025-12-23
 🧩 Description:
    API client for Active Calls module.
 ────────────────────────────────────────────────────────────────*/

import { api } from '../../lib/api';

export interface ActiveCall {
    uuid: string;
    direction: string;
    created: string;
    created_epoch: number;
    name: string;
    state: string;
    cid_name: string;
    cid_num: string;
    ip_addr: string;
    dest: string;
    application: string;
    application_data: string;
    dialplan: string;
    context: string;
    read_codec: string;
    read_rate: number;
    write_codec: string;
    write_rate: number;
    secure: string;
    hostname: string;
    presence_id: string;
    callstate: string;
    callee_name: string;
    callee_num: string;
    callee_direction: string;
    call_uuid: string;
    duration?: number;
}

interface ApiResponse<T> {
    success: boolean;
    data: T;
    pagination?: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

export interface ActiveCallsParams {
    page?: number;
    limit?: number;
    search?: string;
    direction?: string;
    domainId?: number;
}

/**
 * Fetch active calls with pagination
 */
export const fetchActiveCalls = async (params: ActiveCallsParams = {}): Promise<ApiResponse<ActiveCall[]>> => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.set('page', params.page.toString());
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.search) queryParams.set('search', params.search);
    if (params.direction) queryParams.set('direction', params.direction);
    if (params.domainId != null) queryParams.set('domainId', params.domainId.toString());

    const query = queryParams.toString();
    const url = `/api/telephony/active-calls${query ? `?${query}` : ''}`;

    const response = await api.get<ApiResponse<ActiveCall[]>>(url);
    return response.data;
};

/**
 * Refresh active calls data
 */
export const refreshActiveCalls = async (): Promise<{ message: string }> => {
    const response = await api.post<ApiResponse<{ message: string }>>('/api/telephony/active-calls/refresh');
    return response.data.data;
};
